import { AlertTriangle, Loader2, Lock } from 'lucide-react';

import type { ApprovePaymentRunLockParams } from '~entities/payment/api/reporting-payment-run-review';

import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

import { formatMoney, formatScheduledRunEastern } from './payment-run-format';

export interface ApproveModalPreview {
  chargesToProcess: number;
  totalAmount: number;
  flaggedCount: number;
  flaggedAmount: number;
  paymentMethodsSummary: string;
}

interface ApproveLockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextRunAt: string | undefined;
  currencySymbol: string;
  approveModalPreview: ApproveModalPreview | null;
  showSelectiveCopy: boolean;
  selectedLockableCount: number;
  flaggedItemsAcknowledged: boolean;
  onFlaggedItemsAcknowledgedChange: (value: boolean) => void;
  approveModalError: string;
  isApprovePending: boolean;
  approveModalCanSubmit: boolean;
  submitApproveUsesInvoiceIds: boolean;
  invoiceIdsPendingApprove: Array<string>;
  startDate: string;
  endDate: string;
  approveModalFlaggedCount: number;
  onSubmitApprove: (payload: ApprovePaymentRunLockParams) => void;
}

export function ApproveLockDialog({
  open,
  onOpenChange,
  nextRunAt,
  currencySymbol: symbol,
  approveModalPreview,
  showSelectiveCopy,
  selectedLockableCount,
  flaggedItemsAcknowledged,
  onFlaggedItemsAcknowledgedChange,
  approveModalError,
  isApprovePending,
  approveModalCanSubmit,
  submitApproveUsesInvoiceIds,
  invoiceIdsPendingApprove,
  startDate,
  endDate,
  approveModalFlaggedCount,
  onSubmitApprove,
}: ApproveLockDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-md'>
        <DialogHeader className='space-y-0 border-b border-border px-6 py-4 text-left'>
          <DialogTitle className='text-lg font-semibold text-foreground'>
            Approve Payment Run
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 px-6 py-4'>
          {approveModalPreview && nextRunAt ? (
            <>
              {showSelectiveCopy ? (
                <p className='text-sm text-muted-foreground'>
                  You are approving{' '}
                  <strong className='text-foreground'>
                    {selectedLockableCount}
                  </strong>{' '}
                  selected charge
                  {selectedLockableCount !== 1 ? 's' : ''} for the automatic
                  run. Other lockable charges in this window will not be charged
                  until a later run.
                </p>
              ) : null}
              <div className='rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm'>
                <dl className='space-y-2'>
                  <div className='flex justify-between gap-4'>
                    <dt className='text-muted-foreground'>Charges to process</dt>
                    <dd className='font-semibold text-foreground'>
                      {approveModalPreview.chargesToProcess}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-4'>
                    <dt className='text-muted-foreground'>Total amount</dt>
                    <dd className='font-semibold text-foreground'>
                      {formatMoney(approveModalPreview.totalAmount, symbol)}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-4'>
                    <dt className='text-muted-foreground'>Scheduled</dt>
                    <dd className='text-right font-semibold text-foreground'>
                      {formatScheduledRunEastern(nextRunAt)}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-4'>
                    <dt className='text-muted-foreground'>Payment methods</dt>
                    <dd className='text-right font-semibold text-foreground'>
                      {approveModalPreview.paymentMethodsSummary}
                    </dd>
                  </div>
                </dl>
              </div>
              {approveModalPreview.flaggedCount > 0 && (
                <div className='flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950'>
                  <AlertTriangle
                    className='mt-0.5 size-5 shrink-0 text-amber-600'
                    aria-hidden
                  />
                  <p>
                    <strong>
                      {approveModalPreview.flaggedCount} flagged charge
                      {approveModalPreview.flaggedCount !== 1 ? 's' : ''}
                    </strong>{' '}
                    ({formatMoney(approveModalPreview.flaggedAmount, symbol)})
                    are included in this run. Please confirm you&apos;ve reviewed
                    them.
                  </p>
                </div>
              )}
              {approveModalPreview.flaggedCount > 0 && (
                <label className='flex cursor-pointer items-start gap-3 text-sm leading-snug'>
                  <Checkbox
                    checked={flaggedItemsAcknowledged}
                    onCheckedChange={(v) => {
                      onFlaggedItemsAcknowledgedChange(v === true);
                    }}
                    className='mt-0.5'
                    id='flagged-ack'
                  />
                  <span className='font-medium text-foreground'>
                    I&apos;ve reviewed the flagged items and approve them for
                    processing
                  </span>
                </label>
              )}
              <p className='text-xs text-muted-foreground'>
                You can unlock this run up to 30 minutes before processing. After
                that, it cannot be modified.
              </p>
              {approveModalError ? (
                <p className='text-sm text-destructive' role='alert'>
                  {approveModalError}
                </p>
              ) : null}
            </>
          ) : (
            <p className='text-sm text-muted-foreground'>
              Loading run details…
            </p>
          )}
        </div>
        <DialogFooter className='flex-row justify-between gap-3 border-t border-border bg-muted/20 px-6 py-4 sm:justify-between'>
          <Button
            type='button'
            variant='outline'
            className='border-neutral-300 bg-background'
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isApprovePending}
          >
            Go Back
          </Button>
          <Button
            type='button'
            className='bg-amber-500 font-semibold text-white hover:bg-amber-600 disabled:opacity-50'
            disabled={
              !approveModalPreview || !approveModalCanSubmit || isApprovePending
            }
            onClick={() => {
              onSubmitApprove({
                startDate,
                endDate,
                flaggedItemsAcknowledged:
                  approveModalFlaggedCount > 0
                    ? flaggedItemsAcknowledged
                    : true,
                ...(submitApproveUsesInvoiceIds
                  ? { invoiceIds: invoiceIdsPendingApprove }
                  : {}),
              });
            }}
          >
            {isApprovePending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' aria-hidden />
                Locking…
              </>
            ) : (
              <>
                <Lock className='mr-2 size-4' aria-hidden />
                Approve &amp; Lock
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
